import * as React from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  Label,
  Input,
  Spinner,
  useId,
  makeStyles,
  tokens,
  shorthands,
  Divider,
  Text,
  Avatar,
  Dialog,
  DialogSurface,
  Tooltip,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  Delete24Regular,
  Add24Regular,
  Globe24Regular,
  ShieldCheckmark24Regular,
} from '@fluentui/react-icons';
import { ComingSoonNotice } from './ComingSoon';
import { getBootstrapStatus } from '../../services/bootstrapService';
import { getLicensedSites } from '../../services/onboardingService';
import {
  loadConfiguredSitesFromToolConfig,
  saveConfiguredSitesToToolConfig,
  verifySiteOwnership,
} from '../../services/sharePointService';

const useStyles = makeStyles({
  root: {
    ...shorthands.overflow('hidden'),
    display: 'flex',
    height: '100vh',
  },
  content: {
    ...shorthands.flex(1),
    ...shorthands.padding('16px'),
    display: 'grid',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
    marginTop: '20px',
  },
  siteList: {
    marginTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('10px'),
  },
  siteItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('10px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  siteInfo: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  primaryBadge: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    ...shorthands.padding('2px', '8px'),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    fontSize: '10px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  licenseBadge: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
    ...shorthands.padding('2px', '8px'),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    fontSize: '10px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
});

interface SiteConfig {
  url: string;
  isPrimary: boolean;
  siteName?: string;
}

interface ManageSitesPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
}

export const ManageSitesPanel: React.FC<ManageSitesPanelProps> = ({
  isOpen,
  onDismiss,
}) => {
  const styles = useStyles();
  const inputId = useId('site-url-input');

  const [sites, setSites] = React.useState<SiteConfig[]>([]);
  const [licensedSites, setLicensedSites] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [newSiteUrl, setNewSiteUrl] = React.useState('');
  const [primarySiteUrl, setPrimarySiteUrl] = React.useState<string | null>(
    null,
  );
  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null,
  );
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
  const [siteToDelete, setSiteToDelete] = React.useState<string | null>(null);

  // Load initial data
  React.useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    console.log('[ManageSites] Loading data...');
    setLoading(true);
    setError(null);
    try {
      // 1. Get Primary Site URL from bootstrap status (source of truth)
      const status = await getBootstrapStatus();
      if (!status.primarySiteUrl) {
        throw new Error('Primary site URL not found in bootstrap status.');
      }
      setPrimarySiteUrl(status.primarySiteUrl);

      // 2. Fetch licensed sites (immutable sites from License API)
      const licensed = await getLicensedSites();
      setLicensedSites(licensed);

      // 3. Load configured sites from the list
      const result = await loadConfiguredSitesFromToolConfig(
        status.primarySiteUrl,
      );
      if (!result.success || !result.sites) {
        throw new Error(result.error || 'Failed to load sites.');
      }
      setSites(result.sites);
      console.log('[ManageSites] Loaded sites:', result.sites);
    } catch (err: any) {
      console.error('[ManageSites] Error loading data:', err);
      setError(err.message || 'Failed to load sites.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSite = async () => {
    if (!newSiteUrl || !primarySiteUrl) return;

    setError(null);
    setSuccessMessage(null);

    try {
      // 1. Basic format check
      let urlToAdd = newSiteUrl.trim();
      if (!urlToAdd.startsWith('https://')) {
        urlToAdd = `https://${urlToAdd}`;
      }

      // 2. Check for duplicates
      if (sites.some((s) => s.url.toLowerCase() === urlToAdd.toLowerCase())) {
        throw new Error('This site is already in your list.');
      }

      // 3. Verify ownership/access
      const verification = await verifySiteOwnership(urlToAdd);
      if (verification.error) {
        throw new Error(verification.error);
      }

      // 4. Update local state
      const newSite: SiteConfig = {
        url: urlToAdd,
        isPrimary: false, // Only the initial one is primary, typically
        siteName: verification.siteName,
      };
      const updatedSites = [...sites, newSite];

      // 5. Save to SharePoint
      await saveChanges(updatedSites);

      // Success
      setNewSiteUrl('');
      setSuccessMessage('Site added successfully.');
    } catch (err: any) {
      console.error('[ManageSites] Error adding site:', err);
      setError(err.message);
    }
  };

  const confirmDelete = (url: string) => {
    setSiteToDelete(url);
    setIsDeleteConfirmOpen(true);
  };

  const handleRemoveSite = async () => {
    if (!primarySiteUrl || !siteToDelete) return;

    setIsDeleteConfirmOpen(false);
    setLoading(true);
    try {
      const updatedSites = sites.filter((s) => s.url !== siteToDelete);
      await saveChanges(updatedSites);
      setSuccessMessage('Site removed successfully.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      setSiteToDelete(null);
    }
  };

  const saveChanges = async (updatedSites: SiteConfig[]) => {
    if (!primarySiteUrl) return;

    console.log('[ManageSites] Saving changes...', updatedSites);
    const result = await saveConfiguredSitesToToolConfig(
      primarySiteUrl,
      updatedSites,
      primarySiteUrl,
    );
    if (!result.success) {
      throw new Error(result.error || 'Failed to save changes to SharePoint.');
    }
    setSites(updatedSites);
  };

  return (
    <>
      <Drawer
        type='overlay'
        separator
        open={isOpen}
        onOpenChange={(_, { open }) => !open && onDismiss()}
        position='end'
        size='medium'
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance='subtle'
                aria-label='Close'
                icon={<Dismiss24Regular />}
                onClick={onDismiss}
              />
            }
          >
            Manage Sites
          </DrawerHeaderTitle>
        </DrawerHeader>

        <DrawerBody>
          <div className={styles.content}>
            <Text>
              Manage the SharePoint sites connected to Content Trust Dashboard
              Check.
            </Text>

            {error && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  backgroundColor: '#FEF2F2',
                  color: '#B91C1C',
                  borderRadius: 4,
                  fontSize: '14px',
                }}
              >
                {error}
              </div>
            )}

            {successMessage && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  backgroundColor: '#F0FDF4',
                  color: '#15803D',
                  borderRadius: 4,
                  fontSize: '14px',
                }}
              >
                {successMessage}
              </div>
            )}

            {loading && sites.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '40px 0',
                }}
              >
                <Spinner label='Loading sites...' />
              </div>
            ) : (
              <>
                <div className={styles.field}>
                  <Label htmlFor={inputId} weight='semibold'>
                    Add a new site
                  </Label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Input
                      id={inputId}
                      value={newSiteUrl}
                      onChange={(_, data) => setNewSiteUrl(data.value)}
                      placeholder='https://contoso.sharepoint.com/sites/hr'
                      style={{ flex: 1 }}
                      disabled
                    />
                    <Button
                      appearance='primary'
                      icon={<Add24Regular />}
                      onClick={handleAddSite}
                      disabled
                    >
                      Add
                    </Button>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <ComingSoonNotice message="Scanning multiple SharePoint sites isn't supported yet, each tenant currently connects to a single primary site. Multi-site support is planned." />
                  </div>
                </div>

                <Divider style={{ margin: '20px 0' }} />

                <Label weight='semibold'>
                  Connected Sites ({sites.length})
                </Label>

                <div className={styles.siteList}>
                  {sites.map((site) => {
                    const isLicensed = licensedSites.some(
                      (l) => l.toLowerCase() === site.url.toLowerCase(),
                    );
                    const isImmutable = site.isPrimary || isLicensed;

                    return (
                      <div key={site.url} className={styles.siteItem}>
                        <div className={styles.siteInfo}>
                          <Avatar
                            icon={<Globe24Regular />}
                            color='brand'
                            aria-label='SharePoint Site'
                          />
                          <div
                            style={{ display: 'flex', flexDirection: 'column' }}
                          >
                            <Text weight='semibold' size={400}>
                              {site.siteName || 'SharePoint Site'}
                            </Text>
                            <Text
                              size={200}
                              style={{ color: tokens.colorNeutralForeground2 }}
                            >
                              {site.url}
                            </Text>
                            <div
                              style={{
                                marginTop: 4,
                                display: 'flex',
                                gap: '8px',
                              }}
                            >
                              {site.isPrimary && (
                                <span className={styles.primaryBadge}>
                                  Primary
                                </span>
                              )}
                              {isLicensed && !site.isPrimary && (
                                <Tooltip
                                  content='Included in your license setup'
                                  relationship='label'
                                >
                                  <div className={styles.licenseBadge}>
                                    <ShieldCheckmark24Regular
                                      style={{ width: 12, height: 12 }}
                                    />
                                    Licensed
                                  </div>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        </div>
                        {!isImmutable && (
                          <Button
                            appearance='subtle'
                            icon={<Delete24Regular />}
                            aria-label='Remove site'
                            onClick={() => confirmDelete(site.url)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </DrawerBody>
      </Drawer>

      <Dialog
        open={isDeleteConfirmOpen}
        onOpenChange={(_, data) => setIsDeleteConfirmOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirm Removal</DialogTitle>
            <DialogContent>
              Are you sure you want to remove this site? This action cannot be
              undone.
            </DialogContent>
            <DialogActions>
              <Button
                appearance='secondary'
                onClick={() => setIsDeleteConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button appearance='primary' onClick={handleRemoveSite}>
                Remove
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};
